import { useEffect, useMemo, useState } from 'react';
import CastButton from './CastButton';
import { usePartyStore } from './store/partyStore';
import {
  SCENE_2D_IDS,
  SCENE_3D_IDS,
  SCENE_DIMENSION,
  VISUAL_STYLE_GROUPS,
  VISUAL_STYLES,
  type SceneDimension,
  type LyricDisplayMode,
  type VisualStyleGroup,
} from './types';

const LYRIC_MODES: Array<{ id: LyricDisplayMode; label: string }> = [
  { id: 'hooks', label: 'Hooks' },
  { id: 'line', label: 'Line' },
  { id: 'full', label: 'Full' },
  { id: 'off', label: 'Off' },
];

interface PartyOverlayProps {
  onStop: () => void;
  onFullscreen: () => void;
}

export default function PartyOverlay({ onStop, onFullscreen }: PartyOverlayProps) {
  const [sceneTab, setSceneTab] = useState<SceneDimension>('3d');
  const [effectGroup, setEffectGroup] = useState<VisualStyleGroup>('live');
  const [mobilePanel, setMobilePanel] = useState<'scenes' | 'effects'>('scenes');
  const {
    currentTrack,
    overlayVisible,
    nextTrack,
    queueTracks,
    preparedTrackIds,
    spotifyDeviceName,
    spotifyDeviceType,
    syncStatus,
    lastSyncAt,
    lyricsStatus,
    inputMode,
    micEnergy,
    micOnset,
    visualStyle,
    setVisualStyle,
    overrides,
    progressMs,
    activeEvent,
    isPlaying,
    beat,
    adjustIntensity,
    toggleLyrics,
    setLyricDisplayMode,
    toggleForceLyricMode,
    toggleBlackout,
    adjustStrobeBoost,
    skipScene,
    toggleSceneLock,
    setForcedScene,
    toggleOverlay,
    registerTap,
  } = usePartyStore();

  const progressLabel = currentTrack
    ? `${formatTime(progressMs)} / ${formatTime(currentTrack.durationMs)}`
    : '0:00 / 0:00';
  const activeScene = overrides.forcedSceneId ?? activeEvent?.sceneId ?? 'neon-tunnel';
  const sceneButtons = useMemo(
    () => (sceneTab === '2d' ? SCENE_2D_IDS : SCENE_3D_IDS),
    [sceneTab]
  );
  const effectButtons = useMemo(
    () => VISUAL_STYLES.filter((style) => style.group === effectGroup),
    [effectGroup]
  );
  const trackProgress = currentTrack?.durationMs
    ? Math.max(0, Math.min(1, progressMs / currentTrack.durationMs))
    : 0;
  const eventProgress = activeEvent
    ? Math.max(0, Math.min(1, (progressMs - activeEvent.startMs) / Math.max(1, activeEvent.endMs - activeEvent.startMs)))
    : 0;
  const preparedCount = queueTracks.filter((track) => track.prepared || preparedTrackIds.includes(track.id)).length;
  const syncAge = lastSyncAt ? Math.max(0, Math.round((Date.now() - lastSyncAt) / 1000)) : null;

  useEffect(() => {
    setSceneTab(SCENE_DIMENSION[activeScene] ?? '3d');
  }, [activeScene]);

  useEffect(() => {
    const currentGroup = VISUAL_STYLES.find((style) => style.id === visualStyle)?.group;
    if (currentGroup) setEffectGroup(currentGroup);
  }, [visualStyle]);

  if (!overlayVisible) {
    return (
      <div className="party-overlay party-overlay--hidden">
        <button type="button" className="party-ui-toggle" onClick={toggleOverlay}>
          Show UI (H)
        </button>
      </div>
    );
  }

  return (
    <div className="party-overlay">
      <div className="party-top-bar">
        <div className="party-now-playing">
          {currentTrack ? (
            <>
              {currentTrack.albumArtUrl && (
                <img src={currentTrack.albumArtUrl} alt="" className="party-art" />
              )}
              <div>
                <div className="party-track-title">{currentTrack.title}</div>
                <div className="party-track-artist">{currentTrack.artist}</div>
                <div className="party-track-progress">
                  <span style={{ transform: `scaleX(${trackProgress})` }} />
                </div>
              </div>
            </>
          ) : (
            <span className="party-idle">Waiting for Spotify playback…</span>
          )}
        </div>

        <div className="party-top-actions">
          <CastButton />
          <button type="button" className="party-btn" onClick={toggleOverlay}>Hide UI (H)</button>
          <button type="button" className="party-btn" onClick={onFullscreen}>Fullscreen (F)</button>
          <button type="button" className="party-btn party-btn--stop" onClick={onStop}>Stop</button>
        </div>
      </div>

      <div className="party-side-panel">
        <div className="party-panel-block">
          <div className="party-panel-label">Next</div>
          <div className="party-panel-value">
            {nextTrack ? `${nextTrack.title} — ${nextTrack.artist}` : '—'}
          </div>
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Device</div>
          <div className="party-panel-value">
            {spotifyDeviceName
              ? `${spotifyDeviceName}${spotifyDeviceType ? ` · ${spotifyDeviceType}` : ''}`
              : 'No active device'}
          </div>
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Input</div>
          <div className="party-panel-value">{labelize(inputMode)}</div>
          {inputMode === 'mic' && (
            <>
              <div className="party-mic-meter" aria-label="Microphone level">
                <span style={{ transform: `scaleX(${Math.max(0.03, Math.min(1, micEnergy * 2.6))})` }} />
              </div>
              <div className="party-panel-subvalue">
                Level {Math.round(micEnergy * 100)} · Hit {Math.round(micOnset * 100)}
              </div>
            </>
          )}
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Sync</div>
          <div className={`party-panel-value party-sync-${syncStatus}`}>
            {labelize(syncStatus)}
            {syncAge !== null ? ` · ${syncAge}s` : ''}
          </div>
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Lyrics</div>
          <div className={`party-panel-value party-lyrics-${lyricsStatus}`}>
            {overrides.lyricDisplayMode === 'off' || overrides.lyricsEnabled === false
              ? 'Disabled'
              : lyricsStatus === 'synced'
                ? `Synced · ${labelize(overrides.lyricDisplayMode)}`
                : lyricsStatus === 'loading'
                  ? 'Loading…'
                  : 'No synced lyrics'}
          </div>
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">BPM</div>
          <div className="party-panel-value">{Math.round(beat.bpm)} {isPlaying ? 'PLAYING' : 'PAUSED'}</div>
          {currentTrack?.audioProfile && (
            <div className="party-panel-subvalue">
              {labelize(currentTrack.audioProfile.bpmSource)}
              {currentTrack.audioProfile.bpmSource !== 'fallback'
                ? ` · ${Math.round(currentTrack.audioProfile.confidence * 100)}%`
                : ''}
            </div>
          )}
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Scene</div>
          <div className="party-panel-value">{labelize(activeScene)}</div>
          <div className="party-mini-meter">
            <span style={{ transform: `scaleX(${eventProgress})` }} />
          </div>
          {activeEvent && (
            <div className="party-panel-subvalue">
              {labelize(activeEvent.section)} · {Math.round(activeEvent.intensity * 100)}%
            </div>
          )}
        </div>
        <div className="party-panel-block">
          <div className="party-panel-label">Clock</div>
          <div className="party-panel-value">{progressLabel}</div>
        </div>
      </div>

      {queueTracks.length > 0 && (
        <div className="party-queue-panel">
          <div className="party-queue-head">
            <span>Queue Prep</span>
            <span>{preparedCount}/{queueTracks.length}</span>
          </div>
          <div className="party-queue-list">
            {queueTracks.slice(0, 5).map((track) => {
              const prepared = track.prepared || preparedTrackIds.includes(track.id);
              return (
                <div key={track.id} className="party-queue-item">
                  {track.albumArtUrl ? (
                    <img src={track.albumArtUrl} alt="" className="party-queue-art" />
                  ) : (
                    <span className="party-queue-art party-queue-art--empty" />
                  )}
                  <div className="party-queue-copy">
                    <span>{track.title}</span>
                    <small>{track.artist || 'Unknown artist'}</small>
                  </div>
                  <span className={`party-prep-badge${prepared ? ' party-prep-badge--ready' : ''}`}>
                    {prepared ? 'Ready' : 'Prep'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="party-mobile-panel-tabs" role="tablist" aria-label="Visualizer panel">
        <button
          type="button"
          role="tab"
          aria-selected={mobilePanel === 'scenes'}
          className={`party-mobile-panel-tab${mobilePanel === 'scenes' ? ' party-mobile-panel-tab--active' : ''}`}
          onClick={() => setMobilePanel('scenes')}
        >
          Scenes
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePanel === 'effects'}
          className={`party-mobile-panel-tab${mobilePanel === 'effects' ? ' party-mobile-panel-tab--active' : ''}`}
          onClick={() => setMobilePanel('effects')}
        >
          Effects
        </button>
      </div>

      <div className={`party-scene-strip${mobilePanel === 'scenes' ? ' party-mobile-panel--active' : ''}`}>
        <div className="party-scene-tabs" role="tablist" aria-label="Scene dimension">
          {(['3d', '2d'] as SceneDimension[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={sceneTab === tab}
              className={`party-scene-tab${sceneTab === tab ? ' party-scene-tab--active' : ''}`}
              onClick={() => setSceneTab(tab)}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="party-scene-grid">
          {sceneButtons.map((sceneId) => (
            <button
              key={sceneId}
              type="button"
              className={`party-scene-btn${activeScene === sceneId ? ' party-scene-btn--active' : ''}`}
              onClick={() => {
                setForcedScene(sceneId);
                if (!overrides.sceneLocked) toggleSceneLock();
              }}
            >
              {labelize(sceneId)}
            </button>
          ))}
        </div>
      </div>

      <div className={`party-effects-panel${mobilePanel === 'effects' ? ' party-mobile-panel--active' : ''}`}>
        <div className="party-effects-tabs" role="tablist" aria-label="Effects">
          {VISUAL_STYLE_GROUPS.map((group) => (
            <button
              key={group.id}
              type="button"
              role="tab"
              aria-selected={effectGroup === group.id}
              className={`party-effects-tab${effectGroup === group.id ? ' party-effects-tab--active' : ''}`}
              onClick={() => setEffectGroup(group.id)}
            >
              {group.label}
            </button>
          ))}
        </div>
        <div className="party-effects-grid">
          {effectButtons.map((style) => {
            const globalIndex = VISUAL_STYLES.findIndex((item) => item.id === style.id);
            return (
              <button
                key={style.id}
                type="button"
                className={`party-effect-btn${visualStyle === style.id ? ' party-effect-btn--active' : ''}`}
                onClick={() => setVisualStyle(style.id)}
                title={globalIndex >= 0 && globalIndex < 10 ? `${globalIndex === 9 ? 0 : globalIndex + 1}` : undefined}
              >
                {style.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="party-controls-bar">
        <button type="button" className="party-ctrl" onClick={() => adjustIntensity(0.1)}>Intensity +</button>
        <button type="button" className="party-ctrl" onClick={() => adjustIntensity(-0.1)}>Intensity −</button>
        <button type="button" className={`party-ctrl${overrides.lyricsEnabled ? ' party-ctrl--active' : ''}`} onClick={toggleLyrics}>Lyrics</button>
        {LYRIC_MODES.map((mode) => (
          <button
            key={mode.id}
            type="button"
            className={`party-ctrl party-ctrl--compact${overrides.lyricDisplayMode === mode.id ? ' party-ctrl--active' : ''}`}
            onClick={() => setLyricDisplayMode(mode.id)}
          >
            {mode.label}
          </button>
        ))}
        <button type="button" className={`party-ctrl${overrides.forceLyricMode ? ' party-ctrl--active' : ''}`} onClick={toggleForceLyricMode}>Hold Lyric</button>
        <button type="button" className={`party-ctrl${overrides.blackout ? ' party-ctrl--danger' : ''}`} onClick={toggleBlackout}>Blackout</button>
        <button type="button" className="party-ctrl" onClick={() => adjustStrobeBoost(0.15)}>Strobe +</button>
        <button type="button" className="party-ctrl" onClick={skipScene}>Skip Scene</button>
        <button type="button" className={`party-ctrl${overrides.sceneLocked ? ' party-ctrl--active' : ''}`} onClick={toggleSceneLock}>Lock Scene</button>
        <button type="button" className="party-ctrl" onClick={registerTap}>Tap Tempo (T)</button>
      </div>

      <div className="party-shortcuts-hint">
        Keys: +/- intensity · L lyrics · K lyric mode · B blackout · H UI · N skip · O lock · S scene · T tap · F fullscreen · 1-0 effects
      </div>
    </div>
  );
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(Math.max(0, ms || 0) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function labelize(value: string): string {
  return value
    .replace(/-2d$/, '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
